import { WORK_ORDER_NOT_SCHEDULED_REASON } from '../work-order/work-order.interface';
import type { WorkOrderNotScheduledReason } from '../work-order/work-order.interface';
import { ISSUE_STATUS, ISSUE_TYPE } from './issue.interface';
import type { Issue, IssueStatus, IssueType } from './issue.interface';

/** Every issue has the same 3-day resolution target (SWOOP-255: no severity). */
export const ISSUE_TARGET_HOURS = 72;

export const ISSUE_TITLE_MAX = 120;

/** Max length of a description, a resolution note and a comment. */
export const ISSUE_TEXT_MAX = 2000;

const ISSUE_NUMBER_PREFIX = 'ISS-';

const MS_PER_HOUR = 60 * 60 * 1000;

export type IssueTransition = 'reopen' | 'resolve' | 'start';

/** The only status changes a person can make by hand. Anything else is refused by the server. */
export const ISSUE_TRANSITIONS: Record<IssueTransition, { from: IssueStatus; to: IssueStatus }> = {
  reopen: { from: ISSUE_STATUS.RESOLVED, to: ISSUE_STATUS.IN_PROGRESS },
  resolve: { from: ISSUE_STATUS.IN_PROGRESS, to: ISSUE_STATUS.RESOLVED },
  start: { from: ISSUE_STATUS.OPEN, to: ISSUE_STATUS.IN_PROGRESS },
};

export function formatIssueNumber(issueNumber: number): string {
  return `${ISSUE_NUMBER_PREFIX}${issueNumber}`;
}

/**
 * Reads an issue number typed in a search box: `ISS-12`, `iss-12` or `12`. Returns `null` for anything
 * else, so free text like a plate or a title is never mistaken for a number.
 */
export function parseIssueNumber(text: string): number | null {
  const match = /^(?:iss-)?(\d+)$/i.exec(text.trim());
  if (!match) {
    return null;
  }
  const value = Number(match[1]);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

type IssueTiming = Pick<Issue, 'reportedAt' | 'resolvedAt'>;

/** Hours from the reported date to now, or to the resolution once resolved. */
export function issueAgeHours(issue: IssueTiming, now: Date): number {
  const end = issue.resolvedAt ?? now;
  return Math.max(0, (new Date(end).getTime() - new Date(issue.reportedAt).getTime()) / MS_PER_HOUR);
}

/** An unresolved issue older than the target. A resolved issue is never "past target" as an open row. */
export function isIssuePastTarget(issue: IssueTiming & Pick<Issue, 'status'>, now: Date): boolean {
  return issue.status !== ISSUE_STATUS.RESOLVED && issueAgeHours(issue, now) > ISSUE_TARGET_HOURS;
}

/** For a resolved issue: whether it was resolved within the target. */
export function isIssueResolvedWithinTarget(issue: IssueTiming): boolean {
  return issue.resolvedAt != null && issueAgeHours(issue, new Date(issue.resolvedAt)) <= ISSUE_TARGET_HOURS;
}

/**
 * The reason a work order opened FROM an issue gets (SWOOP-305): Damage stays Damage; Fault, Breakdown and
 * Recall become Corrective. The reverse direction (a work order auto-creating its issue) is a separate map.
 */
export const WORK_ORDER_REASON_BY_ISSUE_TYPE: Record<IssueType, WorkOrderNotScheduledReason> = {
  [ISSUE_TYPE.BREAKDOWN]: WORK_ORDER_NOT_SCHEDULED_REASON.CORRECTIVE,
  [ISSUE_TYPE.DAMAGE]: WORK_ORDER_NOT_SCHEDULED_REASON.DAMAGE,
  [ISSUE_TYPE.FAULT]: WORK_ORDER_NOT_SCHEDULED_REASON.CORRECTIVE,
  [ISSUE_TYPE.RECALL]: WORK_ORDER_NOT_SCHEDULED_REASON.CORRECTIVE,
};

/**
 * The statuses an issue may be in to take a work order (SWOOP-305). The single source for both the
 * pre-check ({@link canOpenWorkOrder}) and the backend's conditional link filter, so the two never drift.
 */
export const LINKABLE_ISSUE_STATUSES: readonly IssueStatus[] = [ISSUE_STATUS.OPEN, ISSUE_STATUS.IN_PROGRESS];

/** Whether a work order can be opened from this issue (SWOOP-305): a linkable status, and none linked yet. */
export function canOpenWorkOrder(issue: Pick<Issue, 'status' | 'workOrderId'>): boolean {
  return LINKABLE_ISSUE_STATUSES.includes(issue.status) && !issue.workOrderId;
}
